import { pdf } from '@react-pdf/renderer'
import { ReactPdfCreditNote } from '@sequencehq/invoice-content'
import { downloadBlob, registerReactPdfFonts } from '@sequencehq/utils'
import { isProductionEnv } from 'lib/environment/environment'
import exhaustiveGuard from 'lib/exhaustiveGuard/exhaustiveGuard'
import { useNotifications } from '@sequencehq/core-components'
import { useCallback } from 'react'
import { useFetchDataForLocalPdfCreditNote } from './useFetchDataForLocalPdfCreditNote'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

type CreditNote = Dashboardv99990101Api.GetCreditNote.GetCreditNoteResponse
export type DownloadCreditNotePdfProps = {
  creditNoteId: CreditNote['id']
  creditNoteNumber: CreditNote['creditNoteNumber']
  creditNoteStatus: CreditNote['status']
}

type UseCreditNotePdfDownload = () => {
  downloadPdf: (props: DownloadCreditNotePdfProps) => void
}

registerReactPdfFonts()

export const useCreditNotePdfDownload: UseCreditNotePdfDownload = () => {
  const { displayPromiseNotification } = useNotifications()

  const { fetchData } = useFetchDataForLocalPdfCreditNote()

  /**
   * PDF generation functions
   */
  const localGeneratePdf = useCallback(
    async ({
      creditNoteId,
      creditNoteNumber
    }: {
      creditNoteId: CreditNote['id']
      creditNoteNumber: CreditNote['creditNoteNumber']
    }) => {
      const pdfCreditNoteData = await fetchData({ creditNoteId })

      const blob = await pdf(
        <ReactPdfCreditNote
          creditNote={pdfCreditNoteData.creditNote}
          lineItems={pdfCreditNoteData.lineItems}
          lineItemGroups={pdfCreditNoteData.lineItemGroups}
          merchantDetails={pdfCreditNoteData.merchantDetails}
          showNonProdWatermark={!isProductionEnv()}
          isRenderedInBrowser={true}
        />
      ).toBlob()

      downloadBlob(
        creditNoteNumber ? `${creditNoteNumber}.pdf` : 'Credit note.pdf'
      )(blob)

      return blob
    },
    [fetchData]
  )

  const remoteGeneratePdf = useCallback(
    async ({
      creditNoteId,
      creditNoteNumber
    }: {
      creditNoteId: CreditNote['id']
      creditNoteNumber: CreditNote['creditNoteNumber']
    }) => {
      const creditNotePdfRes = await dashboard20240730Client.getCreditNotePdf({
        id: creditNoteId
      })

      if (!creditNotePdfRes.data) {
        throw new Error('An error occurred while downloading the credit note')
      }

      downloadBlob(
        creditNoteNumber ? `${creditNoteNumber}.pdf` : 'Credit note.pdf'
      )(creditNotePdfRes.data)

      return creditNotePdfRes.data
    },
    []
  )

  const downloadPdf = useCallback(
    async ({
      creditNoteId,
      creditNoteNumber,
      creditNoteStatus
    }: DownloadCreditNotePdfProps) => {
      const downloadOperation = async () => {
        switch (creditNoteStatus) {
          case 'IN_PROGRESS':
          case 'DRAFT':
            return await localGeneratePdf({ creditNoteId, creditNoteNumber })
          case 'FINAL':
          case 'SENT':
          case 'VOIDED':
            return await remoteGeneratePdf({ creditNoteId, creditNoteNumber })
          default:
            return exhaustiveGuard(creditNoteStatus)
        }
      }

      await displayPromiseNotification(downloadOperation(), {
        loading: 'Preparing download',
        success: 'Download ready',
        error: 'PDF download failed'
      })
    },
    [displayPromiseNotification, localGeneratePdf, remoteGeneratePdf]
  )

  return { downloadPdf }
}
